import { lazy, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { Grid, Typography, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { RoundedMedia } from '@client/components/common/Media';
import { getDateTime } from '@sda/utils/date';
import ActPlaceholderImage from '@client/images/placeholders/artist-small.jpg';
import { Hide } from '@client/components/common/Hide';
import { Spacer } from '@client/components/common/Spacer';
import { SignupCTA } from '@client/components/common/SignupCTA';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Description } from '@client/components/common/Description';
import { getImageLargeUrl } from '@sda/utils/image';
import { SoldOutIndicator } from '@client/components/common/SoldOutIndicator';
import { getEventLocation } from '@sda/utils/string';
import { Loader } from '@client/components/common/Loader';
import { useSelector } from '@client/hooks/redux.hooks';
import { getCurrentShowProfiles } from '@client/selectors/entities/shows';
import { PreviewCTA } from './PreviewCTA';
import { TicketCTA } from './TicketCTA';
import { Location } from './Location';
import { ProfileItem } from './ProfileItem';
import { AcceptInvitationCTA } from './AcceptInvitationCTA';
import type { RedeemableTicket } from '@sda/interfaces/ticket';
import type { PublicShow } from '@sda/interfaces/show';
import type { FetchDataType } from '@sda/sdk/interfaces';

const useStyles = makeStyles()((theme) => ({
  sticky: {
    position: 'sticky',
    top: theme.spacing(3),
    marginTop: theme.spacing(8 * -1),

    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(7 * -1),
    },
  },
  main: {
    maxWidth: theme.breakpoints.values.narrow,
  },
  mediaContainer: {
    position: 'relative',
  },
  chipContainer: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
  },
}));

interface ShowProps {
  readonly preview?: boolean;
  readonly ticketRedemption?: RedeemableTicket;
  readonly acceptInvitationCallback?: () => void;
}

const RedemptionCTALoader = lazy(() => import(/* webpackPreload: true */ './RedemptionCTA'));

export const Show: React.FC<ShowProps & PublicShow & FetchDataType> = ({
  preview = false, ticketRedemption, acceptInvitationCallback, ...show
}) => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const showId = show.id;
  let location = getEventLocation(show);

  const profiles = useSelector(getCurrentShowProfiles);

  if (!location && show.compiledAddress) {
    const parts = show.compiledAddress.split(',') || [];

    location = parts[0] || '';
  }

  const applyTz = useCallback((dateStamp) => {
    if (show.timezoneOverride) {
      return getDateTime(dateStamp).setZone(show.timezoneOverride);
    }

    return getDateTime(dateStamp);
  }, [show.timezoneOverride]);

  let CTAComponent = (
    <TicketCTA showId={showId} />
  );

  if (preview) {
    CTAComponent = (
      <PreviewCTA />
    );
  } else if (ticketRedemption) {
    CTAComponent = (
      <Loader
        load={RedemptionCTALoader}
        {...ticketRedemption}
      />
    );
  } else if (acceptInvitationCallback) {
    CTAComponent = (
      <AcceptInvitationCTA callback={acceptInvitationCallback} />
    );
  }

  return (
    <div>
      <Grid
        container
        spacing={isMobile ? 0 : 3}
      >
        <Grid
          item
          xs={12}
          md={8}
          className={classes.main}
        >
          <div className={classes.mediaContainer}>
            <RoundedMedia
              title={show.title || 'Show Title'}
              images={show.publicImageUrl}
              fallback={ActPlaceholderImage}
              sizeCallback={getImageLargeUrl}
              loading="eager"
              enableCarousel
            />

            <div className={classes.chipContainer}>
              <SoldOutIndicator showId={show.id} />
            </div>
          </div>

          <Typography
            variant="h6"
            color={show.title ? 'textPrimary' : 'disabled'}
            paragraph
          >
            {show.title || 'Show Title'}
          </Typography>

          <Typography variant="body1">
            {location ? <Location location={location} /> : (
              <Typography
                color="disabled"
                component="span"
                display="inline"
              >
                Location
              </Typography>
            )}
            ,

            {' '}

            {show.startTime ? applyTz(show.startTime).toFormat('MMMM d, h:mm a') : (
              <Typography
                color="disabled"
                component="span"
                display="inline"
              >
                Date, Time
              </Typography>
            )}
          </Typography>

          <Spacer margin="small" />

          <Stack spacing={3}>
            <Description
              variant="subtitle1"
              color={show.bioHTML ? 'textPrimary' : 'disabled'}
            >
              {show.bioHTML || 'Description'}
            </Description>

            <Hide mdUp>
              {CTAComponent}
            </Hide>

            <SignupCTA />

            {profiles.map((profile) => {
              const hasCompletedProfile = !isEmpty(profile.publicImageUrl) && (!isEmpty(profile.bioHTML) || !isEmpty(profile.description));

              if (!hasCompletedProfile) {
                return null;
              }

              return (
                <ProfileItem
                  {...profile}
                  preview={preview}
                  key={profile.id}
                />
              );
            })}
          </Stack>
        </Grid>

        <Hide mdDown>
          <Grid
            item
            md={4}
          >
            <div className={classes.sticky}>
              {CTAComponent}
            </div>
          </Grid>
        </Hide>
      </Grid>
    </div>
  );
};
